<template>
    <page :busy="busy" :tabs="tabs">
        <grid
            :columnDefs="columnDefs"
            :fetchCallback="fetchCallback"
            ref="grid"
        >
            <responsive-button
                :icon="['fas', 'plus']"
                variant="primary"
                @click="handleCreateDashboardClick"
                >Add Dashboard</responsive-button
            >
        </grid>
        <dashboard-details-modal-form
            :modal-id="dashboardCreateModalID"
            :create="createDashboard"
            :key="dashboardCreateModalKey"
            @success-create="refresh"
        />
        <dashboard-details-modal-form
            :modal-id="dashboardUpdateModalID"
            :initial-values="currentDashboardInitialValues"
            :update="updateDashboard"
            @success-update="refresh"
        />
    </page>
</template>

<script>
import API from '../../../api';
import {
    DASHBOARD_DASHBOARD_BY_ID_ENDPOINT,
    DASHBOARD_DASHBOARD_DEFAULT_ID_ENDPOINT,
    DASHBOARD_DASHBOARD_SET_DEFAULT_ENDPOINT,
    DASHBOARD_DASHBOARD_GRID_ENDPOINT,
    DASHBOARD_DASHBOARD_LIST_ENDPOINT
} from '../../../endpoints';
import { formatResponseError } from '../../../utils/api';
import Grid from '../../generic/grid/grid.vue';
import Page from '../../generic/page/page.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import DashboardDetailsModalForm from './dashboard-details-modal-form.vue';

export default {
    name: 'DashboardList',
    components: {
        ActionsCellRenderer,
        DashboardDetailsModalForm,
        Grid,
        Page,
        ResponsiveButton,
        actionsCellRenderer: ActionsCellRenderer,
    },
    computed: {
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(DASHBOARD_DASHBOARD_GRID_ENDPOINT(), {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                    },
                });
            };
        },
        currentDashboardInitialValues() {
            if (!!this.currentDashboard) {
                return {
                    id: this.currentDashboard.id,
                    name: this.currentDashboard.name,
                };
            }
        },
    },
    data() {
        return {
            busy: false,
            dashboardCreateModalID: 'dashboard-create-modal',
            dashboardCreateModalKey: 0,
            dashboardUpdateModalID: 'dashboard-update-modal',
            currentDashboard: null,
            defaultDashboardID: null,
            tabs: [
                {
                    'title': 'Dashboard List',
                    'route': '/app/dashboard/list/',
                    'active': true
                },
            ],
            columnDefs: [
                {
                    type: 'radioColumn',
                    headerName: 'Default',
                    sortable: false,
                    valueGetter: ({ data }) => this.defaultDashboardID,
                    maxWidth: 85,
                    cellRendererParams: {
                        radioName: 'change-default-dashboard',
                        radioOption: ({ data }) => data.id,
                        onChange: async ({ node, api }, newValue) => {
                            const row = node.data;
                            const defaultDashboardID = newValue;

                            api.showLoadingOverlay();
                            try {
                                await API.post(
                                    DASHBOARD_DASHBOARD_SET_DEFAULT_ENDPOINT(defaultDashboardID)
                                );
                                this.defaultDashboardID = defaultDashboardID;
                            } catch {
                                this.errorToast({ text: `Could not set default dashboard.` });
                                api.redrawRows();
                                return;
                            } finally {
                                api.hideOverlay();
                            }
                        }
                    }
                },
                {
                    field: 'id',
                    type: 'idColumn',
                    maxWidth: 90,
                },
                {
                    field: 'name',
                    headerName: 'Name',
                    cellRenderer: ({ data }) =>
                        `<a href="/app/dashboard/?dashboard=${data.id}">${data.name}</a>`,
                },
                {
                    headerName: '',
                    maxWidth: 130,
                    minWidth: 130,
                    resizable: false,
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        update: (dashboard) => {
                            this.currentDashboard = dashboard;
                            this.$bvModal.show(this.dashboardUpdateModalID);
                        },
                        delete: async (dashboard) => {
                            try {
                                await API.delete(
                                    DASHBOARD_DASHBOARD_BY_ID_ENDPOINT(dashboard.id)
                                );
                            } catch (error) {
                                const { generic } = formatResponseError(error);
                                this.errorToast({ text: generic || `Could not delete dashboard.` });
                                return;
                            }
                            this.refresh();
                        },
                    },
                },
            ],
        }
    },
    mounted() {
        (async () => {
            this.busy = true;

            try {
                await this.fetchDefaultDashboardID();
            } finally {
                this.busy = false;
            }
        })();
    },
    methods: {
        async fetchDefaultDashboardID() {
            try {
                const response = await API.get(DASHBOARD_DASHBOARD_DEFAULT_ID_ENDPOINT());
                this.defaultDashboardID = response.data;
            } catch (err) {
                this.errorToast({ text: 'Could not load default dashboard id.' });
            }
        },
        handleCreateDashboardClick() {
            this.dashboardCreateModalKey += 1;
            this.$nextTick(() => {
                this.$bvModal.show(this.dashboardCreateModalID);
            });
        },
        createDashboard(values) {
            return API.post(DASHBOARD_DASHBOARD_LIST_ENDPOINT(), values);
        },
        updateDashboard({ id, ...values }) {
            return API.patch(DASHBOARD_DASHBOARD_BY_ID_ENDPOINT(id), values);
        },
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
    }
}
</script>